import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";

const UPDATED = "June 5, 2026";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy" },
      {
        name: "description",
        content:
          "How CoolSymbols.cool handles your data: no accounts and no ads. Your favorites, snippets and settings stay in your own browser; we use Google Analytics to understand aggregate usage. Cookies and how to opt out are explained here.",
      },
      { property: "og:title", content: "Privacy Policy" },
      {
        property: "og:description",
        content: "No accounts, no ads. Personalisation stays in your browser; Google Analytics is used for aggregate stats.",
      },
      { property: "og:url", content: abs("/privacy") },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: abs("/privacy") }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">Privacy</span>
        </nav>

        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {UPDATED}</p>

        <div className="prose-policy mt-8 space-y-8 text-foreground/85">
          <section className="rounded-xl border border-border bg-surface/40 p-5">
            <h2 className="font-display text-lg font-semibold text-foreground">The short version</h2>
            <p className="mt-2">
              CoolSymbols.cool (“we”, “us”) is a free website for browsing and copying Unicode symbols
              and generating styled text. We do not require an account and we do not show ads. The
              things you personalise — favorites, saved snippets, your recently-copied list, the
              clipboard tray and your theme — are stored only in your own browser and are never sent to
              us. To understand how the site is used overall, we use Google Analytics, which sets
              cookies and is described below, along with how you can opt out.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Who this applies to</h2>
            <p className="mt-2">
              This policy covers the website at coolsymbols.cool and its tools and pages. By using the
              site you agree to this policy. If you do not agree, please stop using the site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              Information we do not collect
            </h2>
            <p className="mt-2">
              We do not ask for, and the site has no way to receive, your name, email address, phone
              number or payment details. There are no accounts, sign-ups, log-ins or contact forms. We
              do not show advertising, and we do not sell or rent your personal information. The only
              behind-the-scenes data collection is the aggregate usage measurement done by Google
              Analytics, described below.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              Data stored in your browser
            </h2>
            <p className="mt-2">
              To make the site useful, a small amount of data is saved locally using your browser’s{" "}
              <code className="rounded bg-surface px-1">localStorage</code>. This stays on your device
              and is never transmitted to us. It includes:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>Symbols you star as <strong>favorites</strong>;</li>
              <li>Text snippets you <strong>save</strong> from the generators and examples;</li>
              <li>Your <strong>recently-copied</strong> list;</li>
              <li>The contents of your <strong>clipboard tray</strong>;</li>
              <li>Your <strong>theme</strong> preference (light, dark or system).</li>
            </ul>
            <p className="mt-3">
              You are in full control of this data. You can clear it at any time by clearing site data
              for coolsymbols.cool in your browser settings, or by using the in-app controls (for
              example the “Clear” buttons on the Favorites page). Removing it deletes the
              personalisation but does not otherwise affect the site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              Google Analytics
            </h2>
            <p className="mt-2">
              We use Google Analytics 4, a web-analytics service provided by Google LLC (“Google”), to
              understand how visitors use the site in aggregate — for example which pages are popular,
              how people arrive, and general device and browser information — so we can improve it. To
              do this, Google Analytics sets cookies in your browser (typically named{" "}
              <code className="rounded bg-surface px-1">_ga</code> and{" "}
              <code className="rounded bg-surface px-1">_ga_&lt;id&gt;</code>) and collects information
              such as the pages you view and when, the page or site you came from, your approximate
              location, language, and your device, browser and operating system.
            </p>
            <p className="mt-3">
              Your IP address is used by Google to derive a coarse, approximate location and is not
              stored alongside the analytics reports in Google Analytics 4. We use these statistics
              only to understand overall usage; we do not attempt to identify individual visitors, and
              we do not use Google Analytics for advertising or remarketing. The data is processed by
              Google and is also subject to Google’s own terms and privacy policy, and Google may use
              information collected through its services as described in those policies. You can read
              more in{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                Google’s Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                “How Google uses information from sites that use its services”
              </a>
              .
            </p>
            <p className="mt-3">
              <strong>How to opt out.</strong> The analytics cookies are not essential to the site
              working. You can opt out at any time by:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>
                installing the{" "}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>
                ;
              </li>
              <li>blocking or clearing cookies for coolsymbols.cool in your browser settings;</li>
              <li>
                using a browser or extension that blocks analytics, or enabling a “Do Not Track” or
                Global Privacy Control signal.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Cookies</h2>
            <p className="mt-2">
              The only cookies the site uses are the Google Analytics cookies described above, which
              help distinguish visitors and sessions for usage statistics. We do not use cookies for
              advertising or to build profiles about you, and we do not set any other tracking cookies.
              You can block or delete these cookies through your browser settings or the opt-out
              methods above.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              Hosting and server logs
            </h2>
            <p className="mt-2">
              The site is served by our hosting provider, Cloudflare. As with any website, when your
              browser requests a page, Cloudflare’s infrastructure necessarily processes that request
              and may keep short-lived technical logs — such as IP address, browser user-agent and a
              timestamp — to deliver the page, cache content and protect the site against abuse. We do
              not use this information to identify individual visitors. Cloudflare’s handling of this
              data is governed by its own privacy policy.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Shared symbol packs</h2>
            <p className="mt-2">
              When you create a shareable “pack”, the selected symbols are encoded directly into the
              link itself. Nothing is uploaded or stored on a server — the pack exists only in the URL
              you choose to share, and is reconstructed in the recipient’s browser when they open it.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">If you email us</h2>
            <p className="mt-2">
              If you contact us at{" "}
              <a href="mailto:privacy@coolsymbols.cool" className="text-accent hover:underline">
                privacy@coolsymbols.cool
              </a>
              , we will receive your email address and whatever you write to us. We use that only to
              read and reply to your message, and we do not add you to any mailing list.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Children</h2>
            <p className="mt-2">
              The site is general-audience and is not directed at children under 13. We do not
              knowingly collect personal information from children. If you believe a child has provided
              us with personal information, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Your rights</h2>
            <p className="mt-2">
              Depending on where you live, privacy laws such as the GDPR and CCPA give you rights over
              your personal data — including to access, correct or delete it, and to opt out of certain
              processing. The personalisation data on this site lives in your browser and is yours to
              view or erase at any time. For the usage data collected by Google Analytics, you can opt
              out using the methods in the Google Analytics section above, and you can exercise rights
              that Google handles directly through Google’s own tools. We do not sell or share your
              personal information for cross-context behavioural advertising. For anything else, contact
              us using the details below and we will help.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Changes to this policy</h2>
            <p className="mt-2">
              If we change how the site handles data, we will update this page and revise the “last
              updated” date above. Material changes will be reflected here before they take effect.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">Contact</h2>
            <p className="mt-2">
              Questions about this policy? Email{" "}
              <a href="mailto:privacy@coolsymbols.cool" className="text-accent hover:underline">
                privacy@coolsymbols.cool
              </a>
              . See also our{" "}
              <Link to="/terms" className="text-accent hover:underline">
                Terms of Service
              </Link>
              .
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
